/**
 * Sequencer Store
 * Manages sequencer UI state, slots (crates/stopsets), and configuration for sequence generation
 * Persists configuration to localStorage for reusability across sessions
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PlaylistSummary } from '../api/types';

/**
 * Sequencer slot - a droppable zone for playlists (crates) or stopsets
 */
export interface SequencerSlot {
  id: string;
  type: 'crate' | 'stopset';
  playlist: PlaylistSummary | null;
  mode: 'OPTIMIZE' | 'FORCE_NEXT';
  repeatCount: number; // 1-10, how many times slot repeats per iteration
}

/**
 * Sequencer store state interface
 */
interface SequencerStore {
  // UI state
  isOpen: boolean;

  // Slots configuration
  slots: SequencerSlot[];

  // Sequence configuration
  artistSeparation: number;
  outputName: string;
  iterations: number;
  stopMode: 'shortest' | 'all';

  // Actions - UI
  open: () => void;
  close: () => void;

  // Actions - Slot management
  addSlot: () => void;
  removeSlot: (id: string) => void;
  updateSlot: (id: string, playlist: PlaylistSummary) => void;
  updateSlotMode: (id: string, mode: 'OPTIMIZE' | 'FORCE_NEXT') => void;
  updateSlotRepeatCount: (id: string, count: number) => void;
  reorderSlots: (activeId: string, overId: string) => void;
  addStopset: () => void;

  // Actions - Configuration
  setArtistSeparation: (value: number) => void;
  setOutputName: (name: string) => void;
  setIterations: (value: number) => void;
  setStopMode: (mode: 'shortest' | 'all') => void;

  // Reset
  reset: () => void;
}

/**
 * Generate default output name with current date
 */
function getDefaultOutputName(): string {
  const today = new Date().toISOString().split('T')[0];
  return `Sequence ${today}`;
}

/**
 * Sequencer store implementation with localStorage persistence
 */
export const useSequencerStore = create<SequencerStore>()(
  persist(
    (set, get) => ({
  // Initial state
  isOpen: false,
  slots: [],
  artistSeparation: 4,
  outputName: getDefaultOutputName(),
  iterations: 10,
  stopMode: 'shortest',

  // UI actions
  open: () => set({ isOpen: true }),

  close: () => set({ isOpen: false }),

  // Slot management
  addSlot: () => {
    const newSlot: SequencerSlot = {
      id: crypto.randomUUID(),
      type: 'crate',
      playlist: null,
      mode: 'OPTIMIZE',
      repeatCount: 1, // Default to 1
    };
    set({ slots: [...get().slots, newSlot] });
  },

  removeSlot: (id) =>
    set({
      slots: get().slots.filter((slot) => slot.id !== id),
    }),

  updateSlot: (id, playlist) =>
    set({
      slots: get().slots.map((slot) =>
        slot.id === id ? { ...slot, playlist } : slot
      ),
    }),

  updateSlotMode: (id, mode) =>
    set({
      slots: get().slots.map((slot) =>
        slot.id === id ? { ...slot, mode } : slot
      ),
    }),

  updateSlotRepeatCount: (id, count) => {
    const clampedCount = Math.max(1, Math.min(10, count));
    set({
      slots: get().slots.map((slot) =>
        slot.id === id ? { ...slot, repeatCount: clampedCount } : slot
      ),
    });
  },

  reorderSlots: (activeId, overId) =>
    set((state) => {
      const oldIndex = state.slots.findIndex((s) => s.id === activeId);
      const newIndex = state.slots.findIndex((s) => s.id === overId);

      // Safety check - invalid indices
      if (oldIndex === -1 || newIndex === -1) return state;

      const newSlots = [...state.slots];
      const [moved] = newSlots.splice(oldIndex, 1);

      // Safety check - splice returned nothing
      if (!moved) return state;

      newSlots.splice(newIndex, 0, moved);

      return { slots: newSlots };
    }),

  addStopset: () => {
    const newStopset: SequencerSlot = {
      id: crypto.randomUUID(),
      type: 'stopset',
      playlist: null,
      mode: 'FORCE_NEXT', // Stopsets typically force insertion
      repeatCount: 1, // Stopsets also have repeatCount (typically 1)
    };
    set({ slots: [...get().slots, newStopset] });
  },

  // Configuration actions
  setArtistSeparation: (value) => set({ artistSeparation: value }),

  setOutputName: (name) => set({ outputName: name }),

  setIterations: (value) => set({ iterations: value }),

  setStopMode: (mode) => set({ stopMode: mode }),

  // Reset to defaults (keeps overlay open)
  reset: () =>
    set({
      slots: [],
      artistSeparation: 4,
      outputName: getDefaultOutputName(),
      iterations: 10,
      stopMode: 'shortest',
    }),
    }),
    {
      name: 'splister-sequencer-config',
      // Only persist configuration, not UI state
      partialize: (state) => ({
        slots: state.slots,
        artistSeparation: state.artistSeparation,
        outputName: state.outputName,
        iterations: state.iterations,
        stopMode: state.stopMode,
      }),
    }
  )
);
